/**
 * A scene, in the middle of a conversation.
 *
 * The card the technician's line hangs a simulation off: a title, the one
 * thing the student must not misread, and the live thing itself.
 *
 * THE BADGE IS NOT DECORATION. Every scene here runs a general case at
 * ordinary settings and none is wired to the tool the technician is standing
 * at (demoScenes.tsx). Beside a technician's own words that is exactly the
 * thing a student would read as a measurement, so the card says what it is
 * before it says anything else. It is the same argument the offline reply's
 * "on-device" line makes: where a sentence could be mistaken for something
 * it is not, the panel says so rather than hoping.
 *
 * ONE SCENE RUNS AT A TIME. `live` is the panel's decision, not this
 * component's - a transcript with four solvers in it would spend a laptop's
 * whole frame budget on the three nobody is looking at. A card that is not
 * live keeps its title and offers to come back.
 */

import { useEffect, useState, type ComponentType } from 'react'
import { t, tOr } from '../../i18n/i18n'
import { useLang } from '../i18n/LanguageProvider'
import { loadDemoView, type DemoProps } from './demoViews'

export function TalkDemo({
  id,
  live,
  onShow,
  asked,
}: {
  id: string
  live: boolean
  onShow: () => void
  /** The student's question that brought the scene up, for a scene that opens differently depending on it. */
  asked?: string
}) {
  useLang()
  // `null` while the chunk is in the air, `false` when it will not come.
  const [View, setView] = useState<ComponentType<DemoProps> | null | false>(null)

  useEffect(() => {
    if (!live) return
    let alive = true
    const pending = loadDemoView(id)
    if (!pending) {
      setView(false)
      return
    }
    void pending
      .then((component) => {
        // Stored through a setter function: React would otherwise call a
        // component passed to setState as if it were an updater.
        if (alive) setView(() => component)
      })
      .catch(() => {
        if (alive) setView(false)
      })
    return () => {
      alive = false
    }
  }, [id, live])

  const title = tOr(`talk.demo.${id}.Title`, id)
  return (
    <div className={`talk-demo${live ? ' live' : ''}`}>
      <div className="talk-demo-head">
        <div className="talk-demo-title">{title}</div>
        {!live && (
          <button className="talk-demo-show" onClick={onShow}>
            {t('talk.demo.Show')}
          </button>
        )}
      </div>
      {/* WHAT TO LOOK AT, and it is the difference between a simulation and
          a lesson. A technician who says "here, watch this" and walks away
          has handed the student a canvas with atoms jiggling on it; the line
          that says WHICH jiggle is the diffusion event is the whole teaching
          content. Authored per scene in the catalogue, not written by the
          model - it is a claim about what the solver does, and those are
          not the model's to make. */}
      {live && (
        <>
          <div className="talk-demo-watch">{tOr(`talk.demo.${id}.Watch`, '')}</div>
          {/* And what to DO with it. A student told to watch how the atoms
              move "as the temperature changes" and not told where the
              temperature is has been given a video, not an instrument - so
              this line names the actual control by the label printed on it.
              Also spoken, with the line above it, when the voice is on:
              a technician who put a simulation up would say this part out
              loud rather than expect it to be read (talkStore.perform).
              A scene with no control (the thermal budget is scripted end
              to end) has an empty line, and shows nothing rather than a
              sentence explaining there is nothing to do. */}
          {tOr(`talk.demo.${id}.Try`, '') && (
            <div className="talk-demo-try">{tOr(`talk.demo.${id}.Try`, '')}</div>
          )}
        </>
      )}
      {live &&
        (View === false ? (
          <div className="talk-demo-note">{t('talk.demo.Failed')}</div>
        ) : View === null ? (
          <div className="talk-demo-note">{t('talk.demo.Loading')}</div>
        ) : (
          <View asked={asked} />
        ))}
    </div>
  )
}
